import React, { useState } from "react";
import { EyeIcon, PencilSquareIcon, TrashIcon } from "@heroicons/react/24/outline";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const statusBadge = (value) => {
  const normalized = String(value || "").toLowerCase();

  if (normalized.includes("active") || normalized.includes("paid")) {
    return "bg-emerald-50 text-emerald-700 ring-emerald-100";
  }

  if (normalized.includes("block") || normalized.includes("cancel") || normalized.includes("refund")) {
    return "bg-rose-50 text-rose-700 ring-rose-100";
  }

  if (normalized.includes("pending") || normalized.includes("expired")) {
    return "bg-amber-50 text-amber-700 ring-amber-100";
  }

  return "bg-slate-100 text-slate-600 ring-slate-200";
};

const userName = (item) => `${item?.user?.firstname || ""} ${item?.user?.lastname || ""}`.trim() || "Unnamed user";

const UserList = ({ filteredUsers, setSelectedUser }) => {
  const [showModal, setShowModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [userDetails, setUserDetails] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);

  const handleDeleteUser = async (user, type) => {
    const endpoint = type === "temporary" ? `/api/users/${user.user._id}/temp` : `/api/users/${user.user._id}`;

    try {
      const response = await fetch(endpoint, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error("Failed to delete user");
      }

      return await response.json();
    } catch (error) {
      console.error("Error deleting user:", error);
      throw new Error("Failed to delete user");
    }
  };

  const confirmDeleteUser = async (type) => {
    try {
      const response = await handleDeleteUser(userToDelete, type);

      if (response.message === "User temporarily deleted") {
        toast.success("User temporarily deleted.");
      } else if (response.message === "User permanently deleted") {
        toast.success("User permanently deleted.");
      } else {
        toast.error("Failed to delete the user.");
      }
    } catch (error) {
      toast.error("An error occurred while deleting the user.");
    } finally {
      setShowModal(false);
      setUserToDelete(null);
    }
  };

  const handleViewDetails = (user) => {
    setUserDetails(user);
    setShowDetailsModal(true);
  };

  return (
    <section className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
      <div className="mb-5 flex flex-col justify-between gap-3 sm:flex-row sm:items-center">
        <div>
          <h2 className="text-lg font-bold text-slate-950">Users List</h2>
          <p className="text-sm text-slate-500">Review account, subscription, and payment details.</p>
        </div>
        <span className="rounded-full bg-sky-50 px-3 py-1.5 text-sm font-semibold text-sky-700 ring-1 ring-sky-100">
          {filteredUsers.length} users
        </span>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full min-w-[1180px] text-left text-sm">
          <thead>
            <tr className="border-y border-slate-100 bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
              <th className="px-4 py-3 font-semibold">Name</th>
              <th className="px-4 py-3 font-semibold">Email</th>
              <th className="px-4 py-3 font-semibold">Role</th>
              <th className="px-4 py-3 font-semibold">Block</th>
              <th className="px-4 py-3 font-semibold">Subscription</th>
              <th className="px-4 py-3 font-semibold">End Date</th>
              <th className="px-4 py-3 font-semibold">Plan</th>
              <th className="px-4 py-3 font-semibold">Payment</th>
              <th className="px-4 py-3 font-semibold">Status</th>
              <th className="px-4 py-3 font-semibold">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {filteredUsers.length === 0 ? (
              <tr>
                <td colSpan={10} className="px-4 py-10 text-center text-sm text-slate-500">
                  No users found.
                </td>
              </tr>
            ) : (
              filteredUsers.map((user) => (
                <tr
                  key={user?.user?._id || user?.user?.username}
                  className="cursor-pointer text-slate-700 transition hover:bg-slate-50"
                  onClick={() => setSelectedUser(user)}
                >
                  <td className="px-4 py-4">
                    <div className="font-semibold text-slate-950">{userName(user)}</div>
                  </td>
                  <td className="px-4 py-4 text-slate-500">{user?.user?.username || ""}</td>
                  <td className="px-4 py-4">{user?.user?.role || ""}</td>
                  <td className="px-4 py-4">
                    <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ring-1 ${statusBadge(user?.user?.blockStatus)}`}>
                      {user?.user?.blockStatus || "Unknown"}
                    </span>
                  </td>
                  <td className="px-4 py-4">
                    <span
                      className={`rounded-full px-2.5 py-1 text-xs font-semibold ring-1 ${statusBadge(
                        user?.subscriber?.subscriptionStatus
                      )}`}
                    >
                      {user?.subscriber?.subscriptionStatus || "None"}
                    </span>
                  </td>
                  <td className="px-4 py-4">{user?.subscriber?.subscriptionEndDate || ""}</td>
                  <td className="px-4 py-4">{user?.subscriber?.currentPlan || ""}</td>
                  <td className="px-4 py-4">{user?.payment?.paymentType || ""}</td>
                  <td className="px-4 py-4">
                    <span
                      className={`rounded-full px-2.5 py-1 text-xs font-semibold ring-1 ${statusBadge(
                        user?.payment?.transactionStatus
                      )}`}
                    >
                      {user?.payment?.transactionStatus || "Unknown"}
                    </span>
                  </td>
                  <td className="px-4 py-4">
                    <div className="flex items-center gap-2">
                      <button
                        className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 text-slate-600 transition hover:bg-slate-50"
                        onClick={(event) => {
                          event.stopPropagation();
                          setSelectedUser(user);
                        }}
                        aria-label="Edit user"
                      >
                        <PencilSquareIcon className="h-5 w-5" />
                      </button>
                      <button
                        className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 text-slate-600 transition hover:bg-slate-50"
                        onClick={(event) => {
                          event.stopPropagation();
                          handleViewDetails(user);
                        }}
                        aria-label="View user details"
                      >
                        <EyeIcon className="h-5 w-5" />
                      </button>
                      <button
                        className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-rose-100 bg-rose-50 text-rose-600 transition hover:bg-rose-100"
                        onClick={(event) => {
                          event.stopPropagation();
                          setUserToDelete(user);
                          setShowModal(true);
                        }}
                        aria-label="Delete user"
                      >
                        <TrashIcon className="h-5 w-5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/50 p-4">
          <div className="w-full max-w-md rounded-lg border border-slate-200 bg-white p-5 shadow-xl">
            <h3 className="text-lg font-bold text-slate-950">Delete user</h3>
            <p className="mt-2 text-sm text-slate-500">
              Choose how you want to delete {userToDelete ? userName(userToDelete) : "this user"}.
            </p>
            <div className="mt-5 grid gap-3 sm:grid-cols-2">
              <button
                className="rounded-lg bg-amber-500 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-amber-600"
                onClick={() => confirmDeleteUser("temporary")}
              >
                Temporary Delete
              </button>
              <button
                className="rounded-lg bg-rose-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-rose-700"
                onClick={() => confirmDeleteUser("permanent")}
              >
                Permanent Delete
              </button>
            </div>
            <div className="mt-4 flex justify-end">
              <button
                className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
                onClick={() => setShowModal(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {showDetailsModal && userDetails && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/50 p-4">
          <div className="w-full max-w-2xl rounded-lg border border-slate-200 bg-white p-5 shadow-xl">
            <div className="mb-5">
              <h3 className="text-lg font-bold text-slate-950">User Details</h3>
              <p className="text-sm text-slate-500">{userName(userDetails)}</p>
            </div>

            <dl className="grid gap-3 sm:grid-cols-2">
              {[
                ["Email", userDetails?.user?.username],
                ["Role", userDetails?.user?.role],
                ["Block Status", userDetails?.user?.blockStatus],
                ["Subscription", userDetails?.subscriber?.subscriptionStatus],
                ["End Date", userDetails?.subscriber?.subscriptionEndDate],
                ["Plan", userDetails?.subscriber?.currentPlan],
                ["Payment Type", userDetails?.payment?.paymentType],
                ["Transaction", userDetails?.payment?.transactionStatus],
              ].map(([label, value]) => (
                <div key={label} className="rounded-lg border border-slate-100 bg-slate-50 p-3">
                  <dt className="text-xs font-semibold uppercase text-slate-500">{label}</dt>
                  <dd className="mt-1 text-sm font-semibold text-slate-950">{value || "Not available"}</dd>
                </div>
              ))}
            </dl>

            <div className="mt-5 flex justify-end">
              <button
                className="rounded-lg bg-slate-950 px-4 py-2 text-sm font-semibold text-white transition hover:bg-slate-800"
                onClick={() => setShowDetailsModal(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      <ToastContainer position="top-right" autoClose={3000} hideProgressBar newestOnTop closeOnClick pauseOnFocusLoss draggable pauseOnHover />
    </section>
  );
};

export default UserList;
